import { useMemo } from 'react';
import { buildAppleMapDirectionsLink, buildAppleMapsAddressLink } from '@trinity/chat-components/lib/apple.maps';
import { buildGoogleMapDirectionsLink, buildGoogleMapsAddressLink } from '@trinity/chat-components/lib/google.maps';
import { type BuildMapLinkParams, type LocationDescriptor } from '@trinity/chat-components/lib/interfaces';
import appleMapsIcon from '@trinity/chat-components/static/logos/apple-maps.png';
import googleMapsIcon from '@trinity/chat-components/static/logos/google-maps.png';
import { device } from '@trinity/chat-components/utils';

type DirectionsParams = {
  mapParams: BuildMapLinkParams;
};

type AppStore = {
  link: string;
  logo: string;
};
export type MapsLinkResult = {
  mapsLink: string;
  appStoreLink: string;
  icon: string;
  title: string;
};

export const useMapDirectionsLinks = ({ mapParams }: DirectionsParams) => {
  return useMemo(() => {
    const isIOS = device.apple.device;
    const isAndroid = device.android.device;

    const sanitizedParams = sanitizeDirectionParams(mapParams);

    const appleMapsLink = buildAppleMapDirectionsLink(sanitizedParams);
    const googleMapsLink = buildGoogleMapDirectionsLink(sanitizedParams);

    const results: MapsLinkResult[] = [
      {
        mapsLink: googleMapsLink,
        icon: googleMapsIcon,
        appStoreLink: (() => {
          if (isAndroid) return ANDROID_GOOGLE_MAPS_APP_STORE.link;
          if (isIOS) return IOS_GOOGLE_MAPS_APP_STORE.link;
          return null;
        })(),
        title: 'Google',
      },
    ];

    // if (isIOS) {
    results.push({
      mapsLink: appleMapsLink,
      appStoreLink: IOS_APPLE_MAPS_APP_STORE.link,
      icon: appleMapsIcon,
      title: 'Apple',
    });
    // }

    return results;

  }, [mapParams]);
};

type AddressParams = {
  address: string;
};

export const useMapAddressLinks = (mapParams: AddressParams) => {
  return useMemo(() => {
    const isIOS = device.apple.device;
    const isAndroid = device.android.device;

    const appleMapsLink = buildAppleMapsAddressLink(mapParams);
    const googleMapsLink = buildGoogleMapsAddressLink(mapParams);

    const results: MapsLinkResult[] = [
      {
        mapsLink: googleMapsLink,
        icon: googleMapsIcon,
        appStoreLink: (() => {
          if (isAndroid) return ANDROID_GOOGLE_MAPS_APP_STORE.link;
          if (isIOS) return IOS_GOOGLE_MAPS_APP_STORE.link;
          return null;
        })(),
        title: 'Google',
      },
    ];

    //if (isIOS) {
    results.push({
      mapsLink: appleMapsLink,
      appStoreLink: IOS_APPLE_MAPS_APP_STORE.link,
      icon: appleMapsIcon,
      title: 'Apple',
    });
    //}

    return results;

  }, [mapParams]);
};

const ANDROID_GOOGLE_MAPS_APP_STORE: AppStore = {
  link: 'https://play.google.com/store/apps/details?id=com.google.android.apps.maps',
  logo: googleMapsIcon,
};
const IOS_GOOGLE_MAPS_APP_STORE: AppStore = {
  link: 'https://apps.apple.com/us/app/google-maps/id585027354',
  logo: googleMapsIcon,
};
const IOS_APPLE_MAPS_APP_STORE: AppStore = {
  link: 'https://apps.apple.com/us/app/apple-maps/id915056765',
  logo: appleMapsIcon,
};

function sanitizeDirectionParams(params: BuildMapLinkParams): BuildMapLinkParams {
  const descriptors: LocationDescriptor[] = [
    params.origin,
    ...(params.waypoints || []),
    params.destination,
  ];

  const deduped = dedupeDescriptors(descriptors);

  if (deduped.length < 2) throw new Error(`At least two unique locations must be provided`);


  return {
    origin: deduped[0],
    destination: deduped[deduped.length - 1],
    waypoints: deduped.slice(1, deduped.length - 1),
  };
}


function dedupeDescriptors(descriptors: LocationDescriptor[]) {
  return descriptors.reduce<LocationDescriptor[]>((acc, current) => {
    if (acc.length === 0) {
      acc.push(current);
    } else {
      const prevItem = acc[acc.length - 1];

      if (JSON.stringify(prevItem) !== JSON.stringify(current)) {
        acc.push(current);
      }
    }

    return acc;
  }, []);
}
